import { useEffect, useState } from 'react'
import { MINIMAL_SKIN } from '@sh/shared'

export interface Skin {
  id: string
  name: string
  /** CSS custom properties applied to :root. */
  tokens: Record<string, string>
  /** Optional imagery, resolved relative to /skins/<id>/. */
  assets?: Record<string, string>
  /** Human credit shown in the About panel. */
  credit?: string
}

/**
 * Used until a skin manifest arrives, and if one ever fails to load. This is
 * deliberately not the default skin: it names no assets at all, so it is the
 * one skin that cannot fail to render. A table whose artwork will not download
 * gets a plain, fully readable board rather than a broken one.
 */
export const FALLBACK_SKIN: Skin = {
  id: MINIMAL_SKIN,
  name: 'Minimal',
  tokens: {},
}

/**
 * Every piece of artwork a skin may name, and where each one is shown. A skin
 * declaring none of them is a complete skin; the components fall back to the
 * token styling for each key they do not get. Kept here so the Storybook
 * gallery can show what the active skin covers and what it leaves plain.
 */
export const SKIN_ASSET_KEYS: { key: string; where: string }[] = [
  { key: 'logo', where: 'the landing page and the table screen' },
  { key: 'tile.liberal', where: 'an enacted policy on the liberal track' },
  { key: 'tile.fascist', where: 'an enacted policy on the fascist track' },
  { key: 'role.liberal', where: 'the Secret Role card, behind the veil' },
  { key: 'role.fascist', where: 'the Secret Role card, behind the veil' },
  { key: 'role.hitler', where: 'the Secret Role card, behind the veil' },
  { key: 'party.liberal', where: 'the Party Membership card an investigation returns' },
  { key: 'party.fascist', where: 'the Party Membership card an investigation returns' },
]

const cache = new Map<string, Skin>()

export async function loadSkin(id: string): Promise<Skin> {
  const cached = cache.get(id)
  if (cached) return cached
  try {
    const res = await fetch(`/skins/${id}/skin.json`)
    if (!res.ok) throw new Error(String(res.status))
    const skin = (await res.json()) as Skin
    cache.set(id, skin)
    warm(skin)
    return skin
  } catch {
    return FALLBACK_SKIN
  }
}

/**
 * Downloads and decodes everything a skin names, as soon as we know its name.
 *
 * Every one of these images is shown at a moment that cannot wait for it. The
 * role card appears under a finger already pressing the screen and is gone the
 * instant that finger lifts; a policy tile lands on the board at the end of an
 * animation the whole table is watching. Fetched on first render, each one
 * shows up a beat late - a blank card behind a held thumb, a tile that pops.
 *
 * The page holds their shape either way (see `--art-*-ratio`), so this is not
 * what stops the layout moving. It is what stops the artwork arriving after
 * the moment it was meant for. A whole skin is a few hundred kilobytes, asked
 * for once per tab, while the lobby is waiting for people to sit down.
 */
function warm(skin: Skin): void {
  if (typeof Image === 'undefined') return
  for (const key of Object.keys(skin.assets ?? {})) {
    const url = assetUrl(skin, key)
    if (!url) continue
    const img = new Image()
    img.decoding = 'async'
    img.src = url
    // A skin that names art it does not ship is already handled - the board
    // falls back to the token styling - so a miss here is nothing to report.
    void img.decode().catch(() => {})
  }
}

/**
 * Applies the lobby-wide skin. Only design tokens are swapped: contrast rules,
 * the letter on each policy tile and its pattern live in the components, so a
 * skin cannot make the board unreadable for a colourblind player.
 *
 * No id means there is no skin to load - skins are off, or the catalogue has
 * not arrived yet - and the fallback stands, unfetched.
 */
export function useSkin(id: string | null | undefined): Skin {
  const [skin, setSkin] = useState<Skin>(FALLBACK_SKIN)

  useEffect(() => {
    if (!id) {
      setSkin(FALLBACK_SKIN)
      return
    }
    let cancelled = false
    void loadSkin(id).then((next) => {
      if (!cancelled) setSkin(next)
    })
    return () => {
      cancelled = true
    }
  }, [id])

  useEffect(() => {
    const root = document.documentElement
    const applied = Object.keys(skin.tokens)
    for (const [token, value] of Object.entries(skin.tokens)) root.style.setProperty(token, value)
    root.dataset.skin = skin.id
    return () => {
      for (const token of applied) root.style.removeProperty(token)
    }
  }, [skin])

  return skin
}

export function assetUrl(skin: Skin, key: string): string | null {
  const path = skin.assets?.[key]
  return path ? `/skins/${skin.id}/${path}` : null
}
